/**
 * @file     SignInForm.tsx
 * @purpose  Sign-in form with inline forgot-password view, shake on error, glassmorphic card.
 */
'use client';

import { useState, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import MuiLink from '@mui/material/Link';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { glassBlur } from '@/design-system/tokens';
import { useRouter, useSearchParams } from 'next/navigation';
import { useAppTheme } from '@/core/theme';
import { useAuth } from '@/hooks/useAuth';
import { safeNextPath } from '@/services/auth/emailLinkOrigin';
import { SocialAuthButtons } from '@/components/gate/SocialAuthButtons';
import { PhoneSignInPanel } from './PhoneSignInPanel';

type View = 'sign-in' | 'forgot-password' | 'phone';

interface SignInFormProps {
  onSwitchAuthMode?: () => void;
  /** Override the redirect target. Used when SignInForm is embedded in a modal
   *  where we don't want to navigate away from the current page on success. */
  redirectOverride?: string;
  /** Called after a successful sign-in instead of router.push. If provided,
   *  the form will not navigate, caller is responsible for closing/handling. */
  onAuthSuccess?: () => void;
}

export const SignInForm = ({ onSwitchAuthMode, redirectOverride, onAuthSuccess }: SignInFormProps = {}) => {
  const { t } = useTranslation();
  const { theme } = useAppTheme();
  const { signIn } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();

  // Initialise with SSR-safe defaults so server HTML and initial hydration agree.
  // useEffect updates from actual URL params after mount, handlers (submit, magic link,
  // router.push) only run after user interaction, so they always see the live ref value.
  const [redirect, setRedirect] = useState('/');
  const redirectRef = useRef('/');
  const [callbackError, setCallbackError] = useState<string | null>(null);

  useEffect(() => {
    const raw = redirectOverride ?? searchParams.get('redirect') ?? searchParams.get('next') ?? '/';
    const safe = safeNextPath(raw, '/');
    redirectRef.current = safe;
    setRedirect(safe);
    setCallbackError(searchParams.get('error'));
    const email = searchParams.get('email');
    if (email) setEmail(email);
  }, [redirectOverride, searchParams]);

  const [view, setView] = useState<View>('sign-in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetMode, setResetMode] = useState<'reset' | 'magic_link'>('reset');
  const [shaking, setShaking] = useState(false);
  const [showMagicLinkFallback, setShowMagicLinkFallback] = useState(false);
  const [magicLinkSending, setMagicLinkSending] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [resendSending, setResendSending] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [callbackErrorVisible, setCallbackErrorVisible] = useState(true);

  // Map callback error codes to friendly copy + whether the situation
  // is recoverable by resending a fresh magic link.
  const callbackBanner = (() => {
    if (!callbackError || !callbackErrorVisible) return null;
    const recoverable = ['link_expired', 'link_consumed', 'link_invalid', 'pkce_failed', 'callback_failed', 'session_expired', 'otp_expired', 'missing_code'].includes(callbackError);
    const copy: Record<string, string> = {
      link_expired: t('auth.signIn.linkExpired', { defaultValue: 'That sign-in link expired. Want a new one?' }),
      link_consumed: t('auth.signIn.linkConsumed', { defaultValue: 'That sign-in link was already used. Want a new one?' }),
      link_invalid: t('auth.signIn.linkInvalid', { defaultValue: 'That sign-in link is not valid. You can get a new one.' }),
      pkce_failed: t('auth.signIn.pkceFailed', { defaultValue: 'That link did not work. You can get a new one.' }),
      callback_failed: t('auth.signIn.callbackFailed', { defaultValue: 'That link did not sign you in. Want a new one?' }),
      missing_code: t('auth.signIn.missingCode', { defaultValue: 'That link is missing a sign-in code. You can get a new one.' }),
      session_expired: t('auth.signIn.sessionExpired', { defaultValue: 'That link expired or you were signed out. Want a new sign-in link?' }),
      otp_expired: t('auth.signIn.linkExpired', { defaultValue: 'That sign-in link expired. Want a new one?' }),
      other_user: t('auth.signIn.otherUser', { defaultValue: 'That link is for a different account. Sign out, then open it again.' }),
      auth_callback_failed: t('auth.signIn.authCallbackFailed', { defaultValue: 'Sign-in did not finish. Try your email and password, or use a sign-in link.' }),
      oauth_failed: t('auth.signIn.oauthFailed', { defaultValue: 'Sign-in with Google or Apple did not finish. Try again.' }),
      config_error: t('auth.signIn.configError', { defaultValue: 'Sign-in is not working right now. Please try again later.' }),
    };
    return {
      message: copy[callbackError] ?? t('auth.signIn.linkGeneric', { defaultValue: 'Sign-in from that link did not work. Try again below.' }),
      recoverable,
    };
  })();

  const requestFreshLink = async (): Promise<'sent' | 'throttled' | 'failed'> => {
    const { secureFetch } = await import('@/services/anti-scraping/secureFetch');
    const res = await secureFetch('/api/auth/resend-magic-link', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, next: redirectRef.current }),
    });
    if (res.status === 429) return 'throttled';
    if (!res.ok) return 'failed';
    return 'sent';
  };

  const handleResend = async () => {
    if (!email) {
      setError(t('auth.signIn.enterEmailForLink', { defaultValue: 'Enter your email to get a new link.' }));
      return;
    }
    setResendSending(true);
    setError(null);
    try {
      const outcome = await requestFreshLink();
      if (outcome === 'throttled') {
        setError(t('auth.signIn.resendThrottled', { defaultValue: 'Try again in a minute.' }));
      } else if (outcome === 'failed') {
        setError(t('auth.signIn.resendFailed', { defaultValue: 'Could not send the link. Try again in a moment.' }));
      } else {
        setResendSent(true);
        setCallbackErrorVisible(false);
      }
    } catch {
      setError(t('auth.signIn.resendFailed', { defaultValue: 'Could not send the link. Try again in a moment.' }));
    } finally {
      setResendSending(false);
    }
  };

  // If the page loaded with a callback error and we have an email, send a fresh
  // link automatically so users don't have to click anything. Both values land
  // via state AFTER mount (they are read from searchParams in an effect), so
  // this must react to them arriving; a once-on-mount effect always saw the
  // empty initial state and never fired.
  const autoResent = useRef(false);
  useEffect(() => {
    if (autoResent.current) return;
    if (callbackError && email && callbackBanner?.recoverable && !resendSent) {
      autoResent.current = true;
      void (async () => {
        // A link can fail after it has already done its job: a scanner burns
        // the OTP but the address is confirmed and a session exists. Mailing
        // another link then teaches the user their password is broken when it
        // is not. Only send when nobody is actually signed in.
        const { getSupabaseBrowserClient } = await import('@/core/supabase/client');
        const supabase = await getSupabaseBrowserClient();
        const { data } = (await supabase?.auth.getUser()) ?? { data: { user: null } };
        if (data?.user) return;
        await handleResend();
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callbackError, email]);

  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const dur = theme.motion.durations;
  const ease = theme.motion.easing;
  const transition = `${dur.normal}ms ${ease.standard}`;

  const triggerShake = () => setShaking(true);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const { error: signInError } = await signIn(email, password);
    setLoading(false);
    if (signInError) {
      // Detect "email not confirmed" and offer magic link fallback
      const isNotConfirmed = signInError.toLowerCase().includes('not confirmed') || signInError.toLowerCase().includes('not been confirmed');
      const isInvalidCredentials = signInError.toLowerCase().includes('invalid login') || signInError.toLowerCase().includes('invalid credentials');
      if (isNotConfirmed) {
        // Name the real cause. "Invalid credentials" phrasing here is what sends
        // people away convinced the password they just chose does not work.
        setError(t('auth.signIn.emailNotConfirmed', { defaultValue: 'This email is not confirmed yet. Open the confirmation email, or get a sign-in link below.' }));
        setShowMagicLinkFallback(true);
      } else if (isInvalidCredentials) {
        setError(signInError);
        setShowMagicLinkFallback(true);
      } else {
        setError(signInError);
      }
      triggerShake();
      return;
    }
    if (onAuthSuccess) {
      onAuthSuccess();
    } else {
      router.push(redirectRef.current);
    }
  };

  const sendSignInLink = async () => {
    if (!email) {
      setError(t('auth.signIn.enterEmailForSignInLink', { defaultValue: 'Enter your email to get a sign-in link.' }));
      emailRef.current?.focus();
      return;
    }
    setError(null);
    setMagicLinkSending(true);
    try {
      const outcome = await requestFreshLink();
      if (outcome === 'throttled') {
        setError(t('auth.signIn.resendThrottled', { defaultValue: 'Try again in a minute.' }));
      } else if (outcome === 'failed') {
        setError(t('auth.signIn.resendFailed', { defaultValue: 'Could not send the link. Try again in a moment.' }));
      } else {
        setMagicLinkSent(true);
      }
    } catch {
      setError(t('auth.signIn.resendFailed', { defaultValue: 'Could not send the link. Try again in a moment.' }));
    }
    setMagicLinkSending(false);
  };

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError(t('auth.forgot.enterEmail', { defaultValue: 'Enter your email address.' }));
      triggerShake();
      return;
    }
    setLoading(true);
    setError(null);
    // Server decides: accounts with a password get a reset link; passwordless
    // accounts (lead-capture / magic-link only) get a sign-in link instead.
    try {
      const { secureFetch } = await import('@/services/anti-scraping/secureFetch');
      const res = await secureFetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (res.status === 429) {
        setError(t('auth.forgot.throttled', { defaultValue: 'Try again in a minute.' }));
        setLoading(false);
        triggerShake();
        return;
      }
      const data = await res.json().catch(() => ({}));
      setResetMode(data?.mode === 'magic_link' ? 'magic_link' : 'reset');
    } catch {
      // Enumeration-safe endpoint always returns ok; on a network error fall
      // back to the reset-link copy rather than blocking the user.
      setResetMode('reset');
    }
    setLoading(false);
    setResetSent(true);
  };

  const switchToForgot = () => {
    setError(null);
    setResetSent(false);
    setView('forgot-password');
  };

  const switchToSignIn = () => {
    setError(null);
    setResetSent(false);
    setView('sign-in');
  };

  const cardSx = {
    // CSS-var colour: these routes prerender in light, so a raw value would
    // hydrate as a mismatched light card in dark mode.
    backgroundColor: `color-mix(in srgb, ${theme.semanticColors.surfaceCard} 60%, transparent)`,
    border: `1px solid ${theme.semanticColors.borderSubtle}`,
    borderRadius: `${theme.layout.radius.lg}px`,
    backdropFilter: glassBlur.strong,
    p: { xs: 5, sm: 6 },
    width: '100%',
    maxWidth: 420,
    mx: 'auto',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: `${theme.layout.form.fieldGap}px`,
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': { borderRadius: `${theme.layout.radius.md}px` },
  };

  const btnSx = {
    textTransform: 'none' as const,
    borderRadius: `${theme.layout.radius.md}px`,
    fontWeight: 500,
    py: 1.2,
    transition: `background-color ${transition}, transform ${dur.fast}ms ${ease.standard}`,
    '&:active': { transform: 'scale(0.98)' },
  };

  const linkSx = {
    color: theme.semanticColors.textMuted,
    cursor: 'pointer',
    fontSize: 13,
    textDecoration: 'none',
    transition: `color ${transition}`,
    '&:hover': { color: theme.semanticColors.interactivePrimary },
  };


  const errorBanner = error ? (
    <Box
      sx={{
        backgroundColor: alpha(theme.rawSemanticColors.statusError, 0.08),
        border: `1px solid ${alpha(theme.rawSemanticColors.statusError, 0.2)}`,
        borderRadius: `${theme.layout.radius.md}px`,
        px: 2,
        py: 1.2,
      }}
    >
      <Typography variant="body2" sx={{ color: theme.semanticColors.statusError, fontSize: 13 }}>
        {error}
      </Typography>
    </Box>
  ) : null;

  if (view === 'phone') {
    return (
      <Box sx={cardSx}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, textAlign: 'center' }}>
          {t('auth.phone.title', { defaultValue: 'Sign in with a text code' })}
        </Typography>
        <PhoneSignInPanel
          inputSx={inputSx}
          btnSx={btnSx}
          linkSx={linkSx}
          onBack={() => setView('sign-in')}
          onSignedIn={() => (onAuthSuccess ? onAuthSuccess() : router.push(redirectRef.current))}
        />
      </Box>
    );
  }

  if (view === 'forgot-password') {
    return (
      <>
        <Box
          component="form"
          onSubmit={handleForgotSubmit}
          onAnimationEnd={() => setShaking(false)}
          className={shaking ? 'relta-form-shake' : undefined}
          sx={cardSx}
        >
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, textAlign: 'center' }}>
            {t('auth.forgot.title', { defaultValue: 'Reset password' })}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.semanticColors.textSecondary, textAlign: 'center', mb: 0.5 }}>
            {t('auth.forgot.subtitle', { defaultValue: "Enter your email to get a reset link." })}
          </Typography>

          {errorBanner}

          {resetSent ? (
            <Box
              sx={{
                backgroundColor: alpha(theme.rawSemanticColors.statusSuccess, 0.08),
                border: `1px solid ${alpha(theme.rawSemanticColors.statusSuccess, 0.2)}`,
                borderRadius: `${theme.layout.radius.md}px`,
                px: 2,
                py: 2,
                textAlign: 'center',
              }}
            >
              <Typography variant="body2" sx={{ color: theme.semanticColors.statusSuccess, fontWeight: 500, mb: 0.5 }}>
                {t('auth.forgot.sentTitle', { defaultValue: 'Check your inbox' })}
              </Typography>
              <Typography variant="body2" sx={{ color: theme.semanticColors.textSecondary, fontSize: 13 }}>
                {resetMode === 'magic_link'
                  ? t('auth.forgot.sentMagicLinkDescription', {
                      defaultValue: "If {{email}} has an account, a sign-in link is on its way.",
                      email,
                    })
                  : t('auth.forgot.sentDescription', {
                      defaultValue: "If {{email}} has an account, a reset link is on its way.",
                      email,
                    })}
              </Typography>
            </Box>
          ) : (
            <>
              <TextField
                label={t('auth.signIn.emailLabel', { defaultValue: 'Email' })}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                fullWidth
                autoComplete="email"
                autoFocus
                sx={inputSx}
              />
              <Button type="submit" variant="contained" fullWidth disabled={loading} sx={btnSx}>
                {loading
                  ? t('auth.forgot.sending', { defaultValue: 'Sending...' })
                  : t('auth.forgot.action', { defaultValue: 'Send reset link' })}
              </Button>
              <Button
                type="button"
                variant="outlined"
                fullWidth
                disabled={loading || !email}
                onClick={async () => {
                  if (!email) return;
                  setLoading(true);
                  setError('');
                  try {
                    const outcome = await requestFreshLink();
                    if (outcome === 'throttled') {
                      setError(t('auth.signIn.resendThrottled', { defaultValue: 'Try again in a minute.' }));
                    } else if (outcome === 'failed') {
                      setError(t('auth.signIn.resendFailed', { defaultValue: 'Could not send the link. Try again in a moment.' }));
                    } else {
                      setResetSent(true);
                      setResetMode('magic_link');
                    }
                  } catch {
                    setError(t('auth.signIn.resendFailed', { defaultValue: 'Could not send the link. Try again in a moment.' }));
                  }
                  setLoading(false);
                }}
                sx={{ ...btnSx, fontWeight: 500 }}
              >
                {t('auth.forgot.magicLink', { defaultValue: 'Email me a sign-in link' })}
              </Button>
            </>
          )}

          <Box sx={{ textAlign: 'center' }}>
            <MuiLink component="button" type="button" variant="body2" onClick={switchToSignIn} sx={linkSx}>
              {t('auth.forgot.backToSignIn', { defaultValue: 'Back to sign in' })}
            </MuiLink>
          </Box>
        </Box>
      </>
    );
  }

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSignIn}
        onAnimationEnd={() => setShaking(false)}
        className={shaking ? 'relta-form-shake' : undefined}
        sx={cardSx}
      >
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, textAlign: 'center' }}>
          {t('auth.signIn.title', { defaultValue: 'Sign in' })}
        </Typography>
        <Typography variant="body2" sx={{ color: theme.semanticColors.textSecondary, textAlign: 'center', mb: 0.5 }}>
          {t('auth.signIn.subtitle', { defaultValue: 'See your saved homes, searches and alerts.' })}
        </Typography>

        {callbackBanner && !resendSent && (
          <Box
            sx={{
              backgroundColor: alpha(theme.rawSemanticColors.statusWarning, 0.08),
              border: `1px solid ${alpha(theme.rawSemanticColors.statusWarning, 0.2)}`,
              borderRadius: `${theme.layout.radius.md}px`,
              px: 2,
              py: 2,
            }}
          >
            <Typography variant="body2" sx={{ color: theme.semanticColors.textPrimary, fontSize: 13, mb: callbackBanner.recoverable ? 1 : 0 }}>
              {callbackBanner.message}
            </Typography>
            {callbackBanner.recoverable && (
              <>
                {!email && (
                  <Typography variant="body2" sx={{ color: theme.semanticColors.textSecondary, fontSize: 13, mb: 1 }}>
                    {t('auth.signIn.enterEmailThenResend', { defaultValue: 'Enter your email above to get a new link.' })}
                  </Typography>
                )}
                <Button
                  size="small"
                  variant="contained"
                  disabled={resendSending || !email}
                  onClick={handleResend}
                  sx={{ ...btnSx, py: 1, fontSize: 13 }}
                >
                  {resendSending
                    ? t('auth.signIn.sendingMagicLink', { defaultValue: 'Sending...' })
                    : t('auth.signIn.sendFreshLink', { defaultValue: 'Send a new link' })}
                </Button>
              </>
            )}
          </Box>
        )}

        {resendSent && (
          <Box
            sx={{
              backgroundColor: alpha(theme.rawSemanticColors.statusSuccess, 0.08),
              border: `1px solid ${alpha(theme.rawSemanticColors.statusSuccess, 0.2)}`,
              borderRadius: `${theme.layout.radius.md}px`,
              px: 2,
              py: 2,
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" sx={{ color: theme.semanticColors.statusSuccess, fontWeight: 500, mb: 0.25 }}>
              {t('auth.signIn.freshLinkSentTitle', { defaultValue: 'Check your inbox' })}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.semanticColors.textSecondary, fontSize: 13 }}>
              {t('auth.signIn.freshLinkSentDescription', {
                defaultValue: 'A new sign-in link was sent to {{email}}.',
                email,
              })}
            </Typography>
          </Box>
        )}

        {errorBanner}

        {showMagicLinkFallback && !magicLinkSent && (
          <Button
            variant="contained"
            fullWidth
            disabled={magicLinkSending || !email}
            onClick={async () => {
              setMagicLinkSending(true);
              try {
                const outcome = await requestFreshLink();
                if (outcome === 'throttled') {
                  setError(t('auth.signIn.resendThrottled', { defaultValue: 'Try again in a minute.' }));
                } else if (outcome === 'failed') {
                  setError(t('auth.signIn.resendFailed', { defaultValue: 'Could not send the link. Try again in a moment.' }));
                } else {
                  setMagicLinkSent(true);
                }
              } catch {
                setError(t('auth.signIn.resendFailed', { defaultValue: 'Could not send the link. Try again in a moment.' }));
              }
              setMagicLinkSending(false);
            }}
            sx={btnSx}
          >
            {magicLinkSending
              ? t('auth.signIn.sendingMagicLink', { defaultValue: 'Sending...' })
              : t('auth.signIn.sendMagicLink', { defaultValue: 'Email me a sign-in link instead' })}
          </Button>
        )}
        {magicLinkSent && (
          <Box sx={{ textAlign: 'center', py: 1 }}>
            <Typography variant="body2" sx={{ color: theme.semanticColors.statusSuccess, fontWeight: 500 }}>
              {t('auth.signIn.magicLinkSentCalm', { defaultValue: 'Sign-in link sent to {{email}}. Check your inbox.', email })}
            </Typography>
          </Box>
        )}

        <SocialAuthButtons next={redirectOverride ?? (onAuthSuccess ? undefined : redirect)} disabled={loading} />

        <TextField inputRef={emailRef} label={t('auth.signIn.emailLabel', { defaultValue: 'Email' })} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required fullWidth autoComplete="email" sx={inputSx} />
        <TextField inputRef={passwordRef} label={t('auth.signIn.passwordLabel', { defaultValue: 'Password' })} type="password" value={password} onChange={(e) => setPassword(e.target.value)} required fullWidth autoComplete="current-password" sx={inputSx} />

        <Button type="submit" variant="contained" fullWidth disabled={loading} sx={btnSx}>
          {loading ? t('auth.signIn.signingIn', { defaultValue: 'Signing in...' }) : t('auth.signIn.action', { defaultValue: 'Sign in' })}
        </Button>

        <Button
          variant="outlined"
          fullWidth
          onClick={() =>
            onSwitchAuthMode
              ? onSwitchAuthMode()
              : router.push(`/sign-up?redirect=${encodeURIComponent(redirectRef.current)}`)
          }
          sx={btnSx}
        >
          {t('auth.signIn.createAccount', { defaultValue: 'Create account' })}
        </Button>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', columnGap: 3, rowGap: 0.5 }}>
          <MuiLink component="button" type="button" variant="body2" onClick={switchToForgot} sx={{ ...linkSx, minHeight: 44 }}>
            {t('auth.signIn.forgotPassword', { defaultValue: 'Forgot password?' })}
          </MuiLink>
          <MuiLink component="button" type="button" variant="body2" onClick={() => { setError(null); setView('phone'); }} sx={{ ...linkSx, minHeight: 44 }}>
            {t('auth.signIn.textMeCode', { defaultValue: 'Text me a code instead' })}
          </MuiLink>
          {!magicLinkSent && !showMagicLinkFallback && (
            <MuiLink
              component="button"
              type="button"
              variant="body2"
              disabled={magicLinkSending}
              onClick={sendSignInLink}
              sx={{ ...linkSx, minHeight: 44 }}
            >
              {magicLinkSending
                ? t('auth.signIn.sendingMagicLink', { defaultValue: 'Sending...' })
                : t('auth.signIn.emailMeLink', { defaultValue: 'Email me a sign-in link' })}
            </MuiLink>
          )}
        </Box>
      </Box>
    </>
  );
};
